import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import { followPlayer, unfollowPlayer } from '../../../features/scout/scoutProfileSlice';
import { normalizePlayerClass } from '../../../utils/normalizePlayerClass';
import { openOnRowClick } from '../../../utils/rowClick';

export const PlayerRow = ({ player, followedPlayers = [], showNewSchool = false, dynamicStatColumns = [], getStatValue = null }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isFollowing, setIsFollowing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Check if player is in followed players list
  useEffect(() => {
    if (player?._id && followedPlayers && followedPlayers.length > 0) {
      const isPlayerFollowed = followedPlayers.some(
        (p) => p._id === player._id || p.id === player._id
      );
      setIsFollowing(isPlayerFollowed);
    } else {
      // Fallback to player.isFollowing if followedPlayers is not available
      setIsFollowing(player.isFollowing || false);
    }
  }, [player, followedPlayers]);

  const handleViewProfile = () => {
    // Use player ID to navigate
    const playerId = player._id || player.id || player.name.toLowerCase().replace(/\s+/g, '-');
    navigate(`/scout/player/${playerId}`, {
      state: {
        from: 'uncommittedTracker',
        playerData: {
          ...player,
          // Ensure team data is included
          team: player.team
        }
      }
    });
  };

  const handleFollowToggle = async (e) => {
    e.stopPropagation();
    const playerId = player._id;

    setIsLoading(true);
    try {
      if (isFollowing) {
        await dispatch(unfollowPlayer(playerId)).unwrap();
        setIsFollowing(false);
      } else {
        await dispatch(followPlayer(playerId)).unwrap();
        setIsFollowing(true);
      }
    } catch (error) {
      console.error('Follow/Unfollow error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Extract data from API response structure - matching new headers
  // Get jpRank from player level or from battingStats/pitchingStats - show "NA" if not available
  const jpRank = player.jpRank || player.rank ||
    (Array.isArray(player.battingStats) ? player.battingStats[0]?.jpRank : player.battingStats?.jpRank) ||
    (Array.isArray(player.pitchingStats) ? player.pitchingStats[0]?.jpRank : player.pitchingStats?.jpRank) ||
    'NA';
  // const year = player.battingStats?.[0]?.seasonYear || player.pitchingStats?.[0]?.seasonYear || player.fieldingStats?.[0]?.seasonYear || '-';
   const year =  '2026'; // Temporarily hardcoding year to 2026
  const toTitleCase = (str) => str ? str.toLowerCase().replace(/\b\w/g, c => c.toUpperCase()) : str;
  const playerName = toTitleCase(`${player.firstName || ''} ${player.lastName || ''}`.trim()) || 'N/A';

  // Handle position - prefer primaryPosition over position
  const position = player.primaryPosition || player.position || '-';

  const currentTeam = player.team?.name || '-';
  const newSchool = player.newSchool || player.committedTo || '-';
  const newSchoolLogo = player.newSchoolLogo || player.committedSchoolLogo || null;
  const playerClass = normalizePlayerClass(player.playerClass || player.class);
  const region = player.hometown || player.region || '-';

  // Get finalScore from battingStats as playerScore - show "NA" if not available
  const playerScore = (() => {
    const stats = Array.isArray(player.battingStats) ? player.battingStats[0] : player.battingStats;
    const score = stats?.finalScore;
    if (score === null || score === undefined || score === '') return 'NA';
    return score;
  })();

  return (
    <tr onClick={openOnRowClick(handleViewProfile)} className="cursor-pointer hover:bg-slate-800/40 transition border-b border-slate-700">
      {/* JP RANK */}
      {/* <td className="px-2 sm:px-3 py-2 sm:py-3 text-white font-semibold text-[10px] sm:text-xs whitespace-nowrap">
        {jpRank}
      </td> */}

      {/* YEAR */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
        {year}
      </td>

      {/* NAME */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-white text-[10px] sm:text-xs whitespace-nowrap">
        {playerName}
      </td>

      {/* POSITION */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
        {position}
      </td>

      {/* TEAM (Current) */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
        {currentTeam}
      </td>

      {/* NEW SCHOOL - Only show when showNewSchool is true */}
      {showNewSchool && (
        <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
          <div className="flex items-center gap-2 justify-center">
            {newSchoolLogo && (
              <img
                src={newSchoolLogo}
                alt={newSchool}
                className="w-5 h-5 sm:w-6 sm:h-6 object-contain rounded"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            )}
            <span>{newSchool}</span>
          </div>
        </td>
      )}

      {/* CLASS */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-300 text-[10px] sm:text-xs whitespace-nowrap">
        {playerClass}
      </td>

      {/* REGION */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-[10px] sm:text-xs whitespace-nowrap">
        {region}
      </td>

      {/* PLAYER SCORE */}
      {/* <td className="px-2 sm:px-3 py-2 sm:py-3 text-cyan-200 font-semibold text-[10px] sm:text-xs whitespace-nowrap">
        {playerScore}
      </td> */}

      {/* Dynamic stat columns */}
      {dynamicStatColumns.map((column, idx) => (
        <td key={`stat-${idx}`} className="px-2 sm:px-3 py-2 sm:py-3 text-gray-400 text-center text-[10px] sm:text-xs whitespace-nowrap">
          {getStatValue ? getStatValue(player, column) : '0'}
        </td>
      ))}

      {/* ACTIONS */}
      <td className="px-2 sm:px-3 py-2 sm:py-3 sticky right-0 bg-slate-900/95 z-10">
        <div className="flex flex-row gap-1 sm:gap-2 min-w-[160px] sm:min-w-[180px]">
          <button
            onClick={handleViewProfile}
            className="border border-gray-400 bg-[#2e3337] text-white py-1 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs leading-tight hover:bg-slate-800 transition whitespace-nowrap"
          >
            View profile
          </button>
          <button
            onClick={handleFollowToggle}
            disabled={isLoading}
            className={`${
              isFollowing
                ? 'bg-gray-600 hover:bg-gray-700'
                : 'bg-cyan-500 hover:bg-cyan-400'
            } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''} text-white font-semibold py-1 px-2 sm:px-3 rounded-md text-[10px] sm:text-xs leading-tight transition whitespace-nowrap flex items-center justify-center gap-1`}
          >
            {isLoading ? (
              <>
                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                <span>{isFollowing ? 'Unfollowing...' : 'Following...'}</span>
              </>
            ) : (
              <span>{isFollowing ? 'Unfollow' : 'Follow'}</span>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
};
